<script lang="ts" setup>

import RoomieTag from '@lahaus-roomie/roomie/src/components/RoomieTag/index.vue'

import type { Props } from './types'

const { t } = useI18n()

const props = defineProps<Props>()

const companyName = props.listing?.raw_listing?.companies?.[0]?.company_name

const { buildImageUrl } = useImsImageProvider()

const development1Image = buildImageUrl('https://media.lahaus.com/uploads/ims/asset/attachment/44887/fachada.jpg', {
  auto: 'compress,enhance,format',
  height: 500
})

const development2Image = buildImageUrl('https://media.lahaus.com/uploads/ims/asset/attachment/44888/paginas_desde_brochure_basalto_narvarte_2405_23-2.jpg', {
  auto: 'compress,enhance,format',
  height: 500
})

const development3Image = buildImageUrl('https://media.lahaus.com/uploads/ims/asset/attachment/44893/sala-comedor.png', {
  auto: 'compress,enhance,format',
  height: 500
})

</script>

<script lang="ts">
export default {
  name: 'VerificationBanner'
}
</script>

<template>
  <section
    class="verification-banner relative flex flex-col mt-72 mt-0 px-24 lg:px-130 py-64 lg:py-100">
    <div class="la-haus-verified-logo flex flex-row gap-4">
      <RoomieTag
        id="la-haus-verified-tag"
        variant="information"
        label=""
        size="lg">
        <template #icon>
          <img src="https://assets.lahaus.com/static/svgs/icons/LaHausVerificadoLogo_2.svg"></img>
        </template>
      </RoomieTag>
    </div>

    <div class="mt-16 lg:mt-24 text-pine-600 flex flex-row gap-16 items-center">
      <div class="w-92 h-64 bg-white rounded-4 p-12 hidden">
        <img
          class="w-full h-full object-contain"
          src="https://lahaus.imgix.net/uploads/real_estate_builder/logo/21/logo.jpg?auto=compress%2Cenhance%2Cformat">
      </div>

      <div class="display-6 lg:display-4">
        {{ companyName }}
      </div>
    </div>

    <div class="la-haus-verification-advantages flex flex-col lg:flex-row justify-center gap-16 lg:gap-40 mt-40 mb-0 lg:mt-56 items-stretch">
      <div class="frame flex flex-col">
        <img src="https://assets.lahaus.com/static/svgs/icons/Tick_2.svg"></img>

        <span class="my-16 hl-3-sb lg:display-7">{{ t('firstBoxTitle') }}</span>

        <span class="body-1-m lg:body-1-sb">{{ t('firstBoxSubtitle') }}</span>
      </div>

      <div class="frame flex flex-col">
        <img src="https://assets.lahaus.com/static/svgs/icons/Tick_2.svg"></img>

        <span class="my-16 hl-3-sb lg:display-7">{{ t('secondBoxTitle') }}</span>

        <span class="body-1-m lg:body-1-sb">{{ t('secondBoxSubtitle') }}</span>
      </div>

      <div class="frame flex flex-col">
        <img src="https://assets.lahaus.com/static/svgs/icons/Tick_2.svg"></img>

        <span class="my-16 hl-3-sb lg:display-7">{{ t('thirdBoxTitle') }}</span>

        <span class="body-1-m lg:body-1-sb">{{ t('thirdBoxSubtitle') }}</span>
      </div>
    </div>

    <div class="builder-info mt-64 lg:mt-80 hidden">
      <div class="flex flex-col lg:flex-row gap-24 lg:gap-40">
        <div class="builder-stats px-24 lg:py-48 rounded-24 border border-pine-300 flex-1">
          <div class="stats-item mb-24">
            <div class="text-pine-600 hl-2-sb lg:display-6">
              +75 años construyendo
            </div>

            <div class="border-b border-pine-300 mt-16" />
          </div>

          <div class="stats-item mb-24">
            <div class="text-pine-600 hl-2-sb lg:display-6">
              +13 proyectos entregados
            </div>

            <div class="border-b border-pine-300 mt-16" />
          </div>

          <div class="text-pine-600 body-1-m lg:hl-3-m">
            Hicimos alianza con este desarrollador por su trayectoria, compromiso y visión que lo hacen un aliado estratégico.
          </div>
        </div>

        <div class="other-developments flex-1">
          <h3 class="text-pine-600 hl-3-sb lg:display-7 mb-24">
            Otros desarrollos de {{ companyName }}:
          </h3>

          <div class="developments-grid grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div class="development-card">
              <div class="relative rounded-16 overflow-hidden h-230">
                <img
                  :src="development1Image"
                  alt="imagenes proyecto"
                  class="carrousel-picture-class mx-auto object-contain pointer-events-none object-cover w-full h-full -z-1">
              </div>

              <div class="text-pine-600 hl-3-m mt-16">
                Reforma
              </div>
            </div>

            <div class="development-card">
              <div class="relative rounded-16 overflow-hidden h-230">
                <img
                  :src="development2Image"
                  alt="imagenes proyecto"
                  class="carrousel-picture-class mx-auto object-contain pointer-events-none object-cover w-full h-full -z-1">
              </div>

              <div class="text-pine-600 hl-3-m mt-16">
                Reforma
              </div>
            </div>

            <div class="development-card">
              <div class="relative rounded-16 overflow-hidden h-230">
                <img
                  :src="development3Image"
                  alt="imagenes proyecto"
                  class="carrousel-picture-class mx-auto object-contain pointer-events-none object-cover w-full h-full -z-1">
              </div>

              <div class="text-pine-600 hl-3-m mt-16">
                Reforma
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.verification-banner {
  background-color: var(--color-gray-400);

  .la-haus-verified-logo {
    gap: 0.48rem;
  }

  .la-haus-verification-advantages {
    .frame {
      width: 100%;
      border-radius: 24px;
      background-color: var(--color-white);
      padding: 2.4rem;

      img {
        width: 48px;
        height: 48px;
      }

      span {
        @apply text-pine-600;
      }
    }
  }

  #la-haus-verified-tag {
    @apply py-12 px-18;

    min-height: 3.3rem;
    max-height: 3.3rem;

    img {
      width: 16.4rem;
      height: 1.6rem;
    }

    @media (width >= 1024px) {
      @apply py-14 px-22;

      min-height: 5rem;

      img {
        width: 19.7rem;
        height: 1.9rem;
      }
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
